import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { usePWAContext } from '@/contexts/PWAContext';
import { logger } from '@/lib/logger';

const STORAGE_KEY = 'maestro-app-release-version';

/**
 * Persistent fallback for the "force update" feature.
 *
 * The Realtime broadcast on `app-updates` only reaches clients that are
 * currently online and connected. This hook adds a database-backed check so
 * any client that opens the app AFTER an admin pushed an update will still
 * see the "Aggiornamento disponibile" banner:
 *
 *   1. On mount, fetch `app_release.version`.
 *   2. Compare with the version saved in localStorage.
 *      - First run ever: just persist the current version (no banner).
 *      - Different value: surface the update banner.
 *   3. Subscribe to postgres_changes so the banner appears live the moment
 *      an admin bumps the version (covers the case where the realtime
 *      broadcast was missed for any reason).
 *
 * The version is consumed (saved to localStorage) only when the user
 * actually applies the update, in `PWAContext.updateApp()`.
 */
export function useAppReleaseCheck() {
  const { setUpdateAvailable } = usePWAContext();
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const evaluate = (remoteVersion: string | null) => {
      if (!remoteVersion) return;
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        // First load on this device — accept current version as baseline,
        // never trigger a banner retroactively.
        localStorage.setItem(STORAGE_KEY, remoteVersion);
        return;
      }
      if (stored !== remoteVersion) {
        logger.info('[AppRelease] New release detected', { stored, remoteVersion });
        setUpdateAvailable(true);
      }
    };

    (async () => {
      try {
        const { data, error } = await supabase
          .from('app_release')
          .select('version')
          .eq('id', true)
          .maybeSingle();
        if (error) {
          logger.warn('[AppRelease] Failed to read app_release', error);
          return;
        }
        evaluate(data?.version ?? null);
      } catch (err) {
        logger.warn('[AppRelease] Unexpected error', err);
      }
    })();

    const channel = supabase
      .channel('app-release-changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'app_release' },
        (payload) => {
          const next = (payload.new as { version?: string } | null)?.version ?? null;
          evaluate(next);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [setUpdateAvailable]);
}

/** Persist the current remote release version locally (called on apply). */
export async function markCurrentReleaseInstalled() {
  try {
    const { data } = await supabase
      .from('app_release')
      .select('version')
      .eq('id', true)
      .maybeSingle();
    if (data?.version) {
      localStorage.setItem(STORAGE_KEY, data.version);
    }
  } catch {
    /* ignore — best-effort */
  }
}
